import {
  type AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  inject,
  linkedSignal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { EllipsisDirective } from '../../directives/ellipsis/ellipsis';
import { ControlsVisibility } from '../../services/controls-visibility/controls-visibility';
import { FeatureFlagsService } from '../../services/feature-flags/feature-flags';
import { FullscreenService } from '../../services/fullscreen/fullscreen';
import { MetadataService } from '../../services/metadata/metadata';
import { ReaderNavigatorService } from '../../services/readium/navigator/reader-navigator';
import { RangeSliderComponent } from '../range-slider/range-slider';
import { NavigationTabsComponent } from '../navigation/navigation-tabs/navigation-tabs';
import { ReaderSettingsTabsComponent } from '../reader-settings/reader-settings-tabs/reader-settings-tabs';

@Component({
  selector: 'nr-footer',
  imports: [
    ReactiveFormsModule,
    RangeSliderComponent,
    EllipsisDirective,
    ReaderSettingsTabsComponent,
    NavigationTabsComponent,
    TranslatePipe,
  ],
  templateUrl: './footer.html',
  styleUrl: './footer.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class FooterComponent implements AfterViewInit {
  private readonly controlsVisibility = inject(ControlsVisibility);
  private readonly navigatorService = inject(ReaderNavigatorService);
  private readonly metadataService = inject(MetadataService);

  private isPendingNavigation = false;

  readonly featureFlags = inject(FeatureFlagsService);
  readonly fullscreenService = inject(FullscreenService);
  readonly navigationProgress = this.navigatorService.progress;
  readonly displayPercent = linkedSignal(() => this.navigationProgress());
  readonly chapterTitle = computed(() => this.navigatorService.title());
  readonly isPreviewMode = linkedSignal(() => this.metadataService.isPreviewMode());
  // As in the previous NubeReader, a student who must answer each page's questions cannot jump.
  readonly isSliderDisabled = computed(
    () => this.isPreviewMode() || this.metadataService.restrictNextPageWithQuestions(),
  );
  readonly sliderControl = new FormControl(this.navigationProgress());
  readonly isShowingActions = this.controlsVisibility.isHeaderFooterVisible;
  readonly footerActionsClass = computed((): { fadeOut: boolean; elementHidden: boolean } => {
    const fadeOut = !this.isShowingActions();
    const elementHidden = false;
    return { fadeOut, elementHidden };
  });

  constructor() {
    effect(() => {
      const currentProgress = this.navigationProgress();
      if (this.sliderControl.value !== currentProgress) {
        this.sliderControl.setValue(currentProgress, { emitEvent: false });
        this.displayPercent.set(currentProgress);
      }
    });

    this.sliderControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((val) => {
      this.displayPercent.set(Number(val));
      this.isPendingNavigation = true;
    });
  }

  ngAfterViewInit(): void {
    this.controlsVisibility.addElementsToGroup('header-footer', [
      document.querySelector('footer.footer')!,
    ]);
  }

  onInteractionEnd(): void {
    if (this.isPendingNavigation) {
      this.isPendingNavigation = false;
      this.navigatorService.goToPercentage(Number(this.sliderControl.value));
    }
  }
}
